import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { ConfirmDialog } from "primereact/confirmdialog";
import { Divider } from "primereact/divider";
import { InputTextarea } from "primereact/inputtextarea";
import { Tag } from "primereact/tag";
import { Toast } from 'primereact/toast';
import { ToggleButton } from "primereact/togglebutton";
import { classNames } from "primereact/utils";
import React, { useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { CarePlanSectionProps, NA, NO, RESIDENT_CARE_PLAN_TYPE, RESIDENT_CARE_PLAN_TYPE_MESSAGES, YES } from "../../../constants/constants";
import { ASSISTANCE_REQUIRED_MESSAGE } from "../../../constants/carePlanConstants";
import SectionGenieDialog from "./SectionGenieDialog";
import {
  TREATMENT_PROGRAM_GENIE_FIELD_DEFINITIONS,
  TREATMENT_PROGRAM_GENIE_QUESTIONS,
  TreatmentProgramGenieSectionValues,
} from "./treatmentProgramGenie";
import { useCarePlanAutoSave } from "./useCarePlanAutoSave";

const TreatmentProgramSection: React.FC<CarePlanSectionProps> = ({
  sectionData, submitButtonClick, backButtonClick, nextButtonClick, showHelpMessage, loadingCarePlan
}) => {
  const [enableEditing, setEnableEditing] = useState<boolean>(false);
  const [showGenieDialog, setShowGenieDialog] = useState<boolean>(false);

  const toast = useRef<Toast>(null);

  const defaultValues = {
    carePlanType: RESIDENT_CARE_PLAN_TYPE.TREATMENT_PROGRAM_THERAPIES,

    anyOxygenUse: sectionData?.anyOxygenUse || false,
    oxygenUseDetails: sectionData?.oxygenUseDetails || '',
    oxygenUseAssistanceDetails: sectionData?.oxygenUseAssistanceDetails || '',

    anyDialysis: sectionData?.anyDialysis || false,
    dialysisDetails: sectionData?.dialysisDetails || '',
    dialysisAssistanceDetails: sectionData?.dialysisAssistanceDetails || '',

    anyBloodThinners: sectionData?.anyBloodThinners || false,
    anyINR_LABMonitoring: sectionData?.anyINR_LABMonitoring || false,
    anyAntiCoagulantTherapy: sectionData?.anyAntiCoagulantTherapy || false,
    bloodThinnersDetails: sectionData?.bloodThinnersDetails || '',
    bloodThinnersAssistanceDetails: sectionData?.bloodThinnersAssistanceDetails || '',

    anyBloodGlucoseMonitoring: sectionData?.anyBloodGlucoseMonitoring || false,
    bloodGlucoseMonitoringDetails: sectionData?.bloodGlucoseMonitoringDetails || '',
    bloodGlucoseMonitoringAssistanceDetails: sectionData?.bloodGlucoseMonitoringAssistanceDetails || '',

    anyInjectionUse: sectionData?.anyInjectionUse || false,
    injectionUseDetails: sectionData?.injectionUseDetails || '',
    injectionUseAssistanceDetails: sectionData?.injectionUseAssistanceDetails || '',

    anyCPAP_BiPAP: sectionData?.anyCPAP_BiPAP || false,
    CPAP_BiPAPDetails: sectionData?.CPAP_BiPAPDetails || '',
    CPAP_BiPAPAssistanceDetails: sectionData?.CPAP_BiPAPAssistanceDetails || '',

    anyNebulizerUse: sectionData?.anyNebulizerUse || false,
    nebulizerUseDetails: sectionData?.nebulizerUseDetails || '',
    nebulizerUseAssistanceDetails: sectionData?.nebulizerUseAssistanceDetails || '',

    anyRangeOfMotionExercises: sectionData?.anyRangeOfMotionExercises || false,
    rangeOfMotionExercisesDetails: sectionData?.rangeOfMotionExercisesDetails || '',
    rangeOfMotionExercisesAssistanceDetails: sectionData?.rangeOfMotionExercisesAssistanceDetails || '',

    anyPT_OT_ST_Therapies: sectionData?.anyPT_OT_ST_Therapies || false,
    PT_OT_ST_TherapiesDetails: sectionData?.PT_OT_ST_TherapiesDetails || '',
    PT_OT_ST_TherapiesAssistanceDetails: sectionData?.PT_OT_ST_TherapiesAssistanceDetails || '',

    anyNurseDelegatedProcedures: sectionData?.anyNurseDelegatedProcedures || false,
    nurseDelegatedProceduresDetails: sectionData?.nurseDelegatedProceduresDetails || '',
    nurseDelegatedProceduresAssistanceDetails: sectionData?.nurseDelegatedProceduresAssistanceDetails || '',

    anyOtherTreatmentPrograms: sectionData?.anyOtherTreatmentPrograms || false,
    otherTreatmentProgramsDetails: sectionData?.otherTreatmentProgramsDetails || '',
    otherTreatmentProgramsAssistanceDetails: sectionData?.otherTreatmentProgramsAssistanceDetails || '',

    anyHomeHealthServices: sectionData?.anyHomeHealthServices || false,
    homeHealthServicesDetails: sectionData?.homeHealthServicesDetails || '',
    homeHealthServicesAssistanceDetails: sectionData?.homeHealthServicesAssistanceDetails || '',

    anyAdultDayHealthPrograms: sectionData?.anyAdultDayHealthPrograms || false,
    adultDayHealthProgramsDetails: sectionData?.adultDayHealthProgramsDetails || '',
    adultDayHealthProgramsAssistanceDetails: sectionData?.adultDayHealthProgramsAssistanceDetails || '',

    anyHospiceAgencyServices: sectionData?.anyHospiceAgencyServices || false,
    hospiceAgencyServicesDetails: sectionData?.hospiceAgencyServicesDetails || '',
    hospiceAgencyServicesAssistanceDetails: sectionData?.hospiceAgencyServicesAssistanceDetails || '',

    anyOtherProgramServices: sectionData?.anyOtherProgramServices || false,
    otherProgramServicesDetails: sectionData?.otherProgramServicesDetails || '',
    otherProgramServicesAssistanceDetails: sectionData?.otherProgramServicesAssistanceDetails || '',

    anyInterventionsForMeaningfullDay: sectionData?.anyInterventionsForMeaningfullDay || false,
    interventionsForMeaningfullDayDetails: sectionData?.interventionsForMeaningfullDayDetails || '',
    interventionsForMeaningfullDayAssistanceDetails: sectionData?.interventionsForMeaningfullDayAssistanceDetails || '',

    anyPhysicalEnablers: sectionData?.anyPhysicalEnablers || false,
    physicalEnablersDetails: sectionData?.physicalEnablersDetails || '',
    physicalEnablersAssistanceDetails: sectionData?.physicalEnablersAssistanceDetails || '',

    anyTreatmentRefusalPlan: sectionData?.anyTreatmentRefusalPlan || false,
    treatmentRefusalPlanDetails: sectionData?.treatmentRefusalPlanDetails || '',
    treatmentRefusalPlanAssistanceDetails: sectionData?.treatmentRefusalPlanAssistanceDetails || '',
  }

  const { control, formState: { errors }, handleSubmit, reset, watch, register, getValues, setValue } = useForm({ defaultValues });
  const watchAnyOxygenUse = watch('anyOxygenUse', defaultValues.anyOxygenUse);
  const watchAnyDialysis = watch('anyDialysis', defaultValues.anyDialysis);
  const watchAnyBloodThinners = watch('anyBloodThinners', defaultValues.anyBloodThinners);
  const watchAnyBloodGlucoseMonitoring = watch('anyBloodGlucoseMonitoring', defaultValues.anyBloodGlucoseMonitoring);
  const watchAnyInjectionUse = watch('anyInjectionUse', defaultValues.anyInjectionUse);
  const watchAnyCPAP_BiPAP = watch('anyCPAP_BiPAP', defaultValues.anyCPAP_BiPAP);
  const watchAnyNebulizerUse = watch('anyNebulizerUse', defaultValues.anyNebulizerUse);
  const watchAnyRangeOfMotionExercises = watch('anyRangeOfMotionExercises', defaultValues.anyRangeOfMotionExercises);
  const watchAnyPT_OT_ST_Therapies = watch('anyPT_OT_ST_Therapies', defaultValues.anyPT_OT_ST_Therapies);
  const watchAnyNurseDelegatedProcedures = watch('anyNurseDelegatedProcedures', defaultValues.anyNurseDelegatedProcedures);
  const watchAnyOtherTreatmentPrograms = watch('anyOtherTreatmentPrograms', defaultValues.anyOtherTreatmentPrograms);
  const watchAnyHomeHealthServices = watch('anyHomeHealthServices', defaultValues.anyHomeHealthServices);
  const watchAnyAdultDayHealthPrograms = watch('anyAdultDayHealthPrograms', defaultValues.anyAdultDayHealthPrograms);
  const watchAnyHospiceAgencyServices = watch('anyHospiceAgencyServices', defaultValues.anyHospiceAgencyServices);
  const watchAnyOtherProgramServices = watch('anyOtherProgramServices', defaultValues.anyOtherProgramServices);
  const watchAnyInterventionsForMeaningfullDay = watch('anyInterventionsForMeaningfullDay', defaultValues.anyInterventionsForMeaningfullDay);
  const watchAnyPhysicalEnablers = watch('anyPhysicalEnablers', defaultValues.anyPhysicalEnablers);
  const watchAnyTreatmentRefusalPlan = watch('anyTreatmentRefusalPlan', defaultValues.anyTreatmentRefusalPlan);

  const getFormErrorMessage = (name) => {
    return errors[name] && <small className="p-error">{errors[name].message}</small>
  };

  const onsubmit = async (data: any) => {
    submitButtonClick && submitButtonClick(data);
  }

  const onManualSave = async (data: any) => {
    submitButtonClick && submitButtonClick(data, { showSuccessToast: true });
  }

  useCarePlanAutoSave({
    enabled: enableEditing,
    baselineData: defaultValues,
    watch,
    handleSubmit,
    onSubmit: onsubmit,
  });

  const subTitleTemplate = (
    <div className="flex flex-column md:flex-row md:align-items-center md:justify-content-between gap-2">
      <div>
        <span className="p-float-label" style={{ cursor: 'pointer' }}
          onClick={() => { showHelpMessage && showHelpMessage(RESIDENT_CARE_PLAN_TYPE_MESSAGES.TREATMENT_PROGRAM_THERAPIES_OVERVIEW_DETAILS) }}>
          Guidance for this section <i className="pi pi-info-circle"></i>
        </span>
      </div>
      <div>
        <span className="p-float-label" style={{ cursor: 'pointer' }}
          onClick={() => { showHelpMessage && showHelpMessage(RESIDENT_CARE_PLAN_TYPE_MESSAGES.TREATMENT_PROGRAM_THERAPIES_ASSISTANCE_DETAILS) }}>
          Assistance guidance for this section <i className="pi pi-info-circle"></i>
        </span>
      </div>
    </div>
  );

  const actions = () => {
    return (
      <div className="flex justify-content-center gap-2">
        <Button label="Prev" icon="pi pi-arrow-left" severity="info" outlined rounded iconPos="left" onClick={backButtonClick} />
        <Button
          label="Use Genie's Help to Fill"
          icon="pi pi-sparkles"
          severity="warning"
          rounded
          onClick={() => {
            setEnableEditing(true);
            setShowGenieDialog(true);
          }}
          visible={enableEditing}
        />
        <Button label="Edit" icon="pi pi-pencil" severity="warning" outlined rounded onClick={() => setEnableEditing(true)} visible={!enableEditing} />
        <Button label="Disable Edit" icon="pi pi-lock" visible={enableEditing} severity="secondary" outlined rounded onClick={() => { setEnableEditing(false); reset(defaultValues); }} />
        <Button label="Save" loading={loadingCarePlan} disabled={!enableEditing} severity="warning" outlined rounded onClick={handleSubmit(onManualSave)} visible={enableEditing} />
        <Button label="Next" icon="pi pi-arrow-right" severity="info" outlined rounded iconPos="right"
          onClick={nextButtonClick} />
      </div>
    )
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1em" }} className="m-2 p-component resident-careplan-section">
      <Toast ref={toast}></Toast>
      <SectionGenieDialog
        visible={showGenieDialog}
        title="Using Genie now"
        questions={TREATMENT_PROGRAM_GENIE_QUESTIONS}
        fieldDefinitions={TREATMENT_PROGRAM_GENIE_FIELD_DEFINITIONS}
        currentValues={getValues() as TreatmentProgramGenieSectionValues}
        successMessage="Treatment programs and therapies care plan updated."
        errorMessage="Unable to update the treatment programs and therapies care plan."
        onHide={() => setShowGenieDialog(false)}
        onApply={async (payload) => {
          const nextValues = { ...getValues(), ...payload.valueUpdates };
          Object.entries(payload.valueUpdates).forEach(([fieldKey, fieldValue]) => {
            setValue(fieldKey as keyof typeof defaultValues, fieldValue as never, { shouldDirty: true });
          });
          await onsubmit(nextValues);
        }}
      />

      <Card className="resident-careplan-card" footer={actions()} header={actions()} subTitle={subTitleTemplate} >
        <div className="">
          <form className="">
            <div className="flex-column flex-wrap ">
              <Divider><Tag severity="warning">Oxygen</Tag></Divider>
              {
                enableEditing ? (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <label className={classNames({ 'p-error': errors.anyOxygenUse })}>Any oxygen usage:</label>
                      <div className="inline-flex m-2 span-color-white">
                        <Controller
                          name="anyOxygenUse"
                          control={control}
                          render={({ field }) => (
                            <ToggleButton onLabel="Yes" offLabel="No"
                              {...field}
                              checked={field.value}
                              onChange={(e) => field.onChange(e.value)} />
                          )}
                        />
                      </div>
                      {getFormErrorMessage('anyOxygenUse')}
                    </div>

                    {
                      watchAnyOxygenUse && (
                        <>
                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="oxygenUseDetails" className={classNames({ 'p-error': errors.oxygenUseDetails })}>
                                Add oxygen usage details:
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="oxygenUseDetails" control={control}
                                rules={{ required: 'Oxygen usage details is required.' }}
                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Add oxygen usage details"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('oxygenUseDetails')}
                          </div>

                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="oxygenUseAssistanceDetails" className={classNames({ 'p-error': errors.oxygenUseAssistanceDetails })}>
                                {ASSISTANCE_REQUIRED_MESSAGE}
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="oxygenUseAssistanceDetails" control={control}

                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Describe any assistance required for oxygen use"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('oxygenUseAssistanceDetails')}
                          </div>
                        </>
                      )}
                  </div>) : (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any oxygen use: </strong>{sectionData?.anyOxygenUse ? YES : NO}</div>
                    {
                      sectionData?.anyOxygenUse && (
                        <>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Oxygen use details: </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.oxygenUseDetails || NA)}</span>
                          </div>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>{ASSISTANCE_REQUIRED_MESSAGE} </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.oxygenUseAssistanceDetails || NA)}</span>
                          </div>
                        </>
                      )
                    }
                  </div>
                )
              }
            </div>

            <div className="flex-column flex-wrap ">
              <Divider><Tag severity="warning">Dialysis</Tag></Divider>
              {
                enableEditing ? (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <label className={classNames({ 'p-error': errors.anyDialysis })}>Any dialysis usage:</label>
                      <div className="inline-flex m-2 span-color-white">
                        <Controller
                          name="anyDialysis"
                          control={control}
                          render={({ field }) => (
                            <ToggleButton onLabel="Yes" offLabel="No"
                              {...field}
                              checked={field.value}
                              onChange={(e) => field.onChange(e.value)} />
                          )}
                        />
                      </div>
                      {getFormErrorMessage('anyDialysis')}
                    </div>

                    {
                      watchAnyDialysis && (
                        <>
                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="dialysisDetails" className={classNames({ 'p-error': errors.dialysisDetails })}>
                                Add dialysis usage details:
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="dialysisDetails" control={control}
                                rules={{ required: 'Dialysis usage details is required.' }}
                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Add dialysis usage details"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('dialysisDetails')}
                          </div>

                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="dialysisAssistanceDetails" className={classNames({ 'p-error': errors.dialysisAssistanceDetails })}>
                                {ASSISTANCE_REQUIRED_MESSAGE}
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="dialysisAssistanceDetails" control={control}

                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Describe any assistance required for dialysis use"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('dialysisAssistanceDetails')}
                          </div>
                        </>
                      )}
                  </div>) : (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any dialysis use: </strong>{sectionData?.anyDialysis ? YES : NO}</div>
                    {
                      sectionData?.anyDialysis && (
                        <>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Dialysis use details: </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.dialysisDetails || NA)}</span>
                          </div>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>{ASSISTANCE_REQUIRED_MESSAGE} </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.dialysisAssistanceDetails || NA)}</span>
                          </div>
                        </>
                      )
                    }
                  </div>
                )
              }
            </div>

            <div className="flex-column flex-wrap ">
              <Divider><Tag severity="warning">Blood thinner</Tag></Divider>
              {
                enableEditing ? (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <label className={classNames({ 'p-error': errors.anyBloodThinners })}>Any blood thinners:</label>
                      <div className="inline-flex m-2 span-color-white">
                        <Controller
                          name="anyBloodThinners"
                          control={control}
                          render={({ field }) => (
                            <ToggleButton onLabel="Yes" offLabel="No"
                              {...field}
                              checked={field.value}
                              onChange={(e) => field.onChange(e.value)} />
                          )}
                        />
                      </div>
                      {getFormErrorMessage('anyBloodThinners')}
                    </div>

                    {
                      watchAnyBloodThinners && (
                        <>
                          <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <label className={classNames({ 'p-error': errors.anyINR_LABMonitoring })}>Any INR LAB monitoring:</label>
                            <div className="inline-flex m-2 span-color-white">
                              <Controller
                                name="anyINR_LABMonitoring"
                                control={control}
                                render={({ field }) => (
                                  <ToggleButton onLabel="Yes" offLabel="No"
                                    {...field}
                                    checked={field.value}
                                    onChange={(e) => field.onChange(e.value)} />
                                )}
                              />
                            </div>
                            {getFormErrorMessage('anyINR_LABMonitoring')}
                          </div>
                          <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <label className={classNames({ 'p-error': errors.anyAntiCoagulantTherapy })}>Any easily bruised/anti-coagulant therapy:</label>
                            <div className="inline-flex m-2 span-color-white">
                              <Controller
                                name="anyAntiCoagulantTherapy"
                                control={control}
                                render={({ field }) => (
                                  <ToggleButton onLabel="Yes" offLabel="No"
                                    {...field}
                                    checked={field.value}
                                    onChange={(e) => field.onChange(e.value)} />
                                )}
                              />
                            </div>
                            {getFormErrorMessage('anyAntiCoagulantTherapy')}
                          </div>
                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="bloodThinnersDetails" className={classNames({ 'p-error': errors.bloodThinnersDetails })}>
                                Add blood thinners usage details:
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="bloodThinnersDetails" control={control}
                                rules={{ required: 'Blood thinners usage details is required.' }}
                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Add blood thinners usage details"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('bloodThinnersDetails')}
                          </div>

                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="bloodThinnersAssistanceDetails" className={classNames({ 'p-error': errors.bloodThinnersAssistanceDetails })}>
                                {ASSISTANCE_REQUIRED_MESSAGE}
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="bloodThinnersAssistanceDetails" control={control}

                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Describe any assistance required for blood thinners use"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('bloodThinnersAssistanceDetails')}
                          </div>
                        </>
                      )}
                  </div>) : (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any blood thinners use: </strong>{sectionData?.anyBloodThinners ? YES : NO}</div>
                    {
                      sectionData?.anyBloodThinners && (
                        <>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any INR LAB monitoring: </strong>{sectionData?.anyINR_LABMonitoring ? YES : NO}</div>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any easily bruised/anti-coagulant therapy: </strong>{sectionData?.anyAntiCoagulantTherapy ? YES : NO}</div>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Blood thinners use details: </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.bloodThinnersDetails || NA)}</span>
                          </div>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>{ASSISTANCE_REQUIRED_MESSAGE} </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.bloodThinnersAssistanceDetails || NA)}</span>
                          </div>
                        </>
                      )
                    }
                  </div>
                )
              }
            </div>

            <div className="flex-column flex-wrap ">
              <Divider><Tag severity="warning">Blood Glucose</Tag></Divider>
              {
                enableEditing ? (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <label className={classNames({ 'p-error': errors.anyBloodGlucoseMonitoring })}>Any blood glucose monitoring:</label>
                      <div className="inline-flex m-2 span-color-white">
                        <Controller
                          name="anyBloodGlucoseMonitoring"
                          control={control}
                          render={({ field }) => (
                            <ToggleButton onLabel="Yes" offLabel="No"
                              {...field}
                              checked={field.value}
                              onChange={(e) => field.onChange(e.value)} />
                          )}
                        />
                      </div>
                      {getFormErrorMessage('anyBloodGlucoseMonitoring')}
                    </div>

                    {
                      watchAnyBloodGlucoseMonitoring && (
                        <>
                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="bloodGlucoseMonitoringDetails" className={classNames({ 'p-error': errors.bloodGlucoseMonitoringDetails })}>
                                Add blood glucose monitoring details:
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="bloodGlucoseMonitoringDetails" control={control}
                                rules={{ required: 'Blood glucose monitoring details is required.' }}
                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Add blood glucose monitoring details"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('bloodGlucoseMonitoringDetails')}
                          </div>

                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="bloodGlucoseMonitoringAssistanceDetails" className={classNames({ 'p-error': errors.bloodGlucoseMonitoringAssistanceDetails })}>
                                {ASSISTANCE_REQUIRED_MESSAGE}
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="bloodGlucoseMonitoringAssistanceDetails" control={control}

                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Describe any assistance required for blood glucose monitoring"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('bloodGlucoseMonitoringAssistanceDetails')}
                          </div>
                        </>
                      )}
                  </div>) : (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any blood glucose monitoring: </strong>{sectionData?.anyBloodGlucoseMonitoring ? YES : NO}</div>
                    {
                      sectionData?.anyBloodGlucoseMonitoring && (
                        <>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Blood glucose monitoring details: </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.bloodGlucoseMonitoringDetails || NA)}</span>
                          </div>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>{ASSISTANCE_REQUIRED_MESSAGE} </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.bloodGlucoseMonitoringAssistanceDetails || NA)}</span>
                          </div>
                        </>
                      )
                    }
                  </div>
                )
              }
            </div>

            <div className="flex-column flex-wrap ">
              <Divider><Tag severity="warning">Injections</Tag></Divider>
              {
                enableEditing ? (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <label className={classNames({ 'p-error': errors.anyInjectionUse })}>Any injection use:</label>
                      <div className="inline-flex m-2 span-color-white">
                        <Controller
                          name="anyInjectionUse"
                          control={control}
                          render={({ field }) => (
                            <ToggleButton onLabel="Yes" offLabel="No"
                              {...field}
                              checked={field.value}
                              onChange={(e) => field.onChange(e.value)} />
                          )}
                        />
                      </div>
                      {getFormErrorMessage('anyInjectionUse')}
                    </div>

                    {
                      watchAnyInjectionUse && (
                        <>
                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="injectionUseDetails" className={classNames({ 'p-error': errors.injectionUseDetails })}>
                                Add injection usage details:
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="injectionUseDetails" control={control}
                                rules={{ required: 'Injection usage details is required.' }}
                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Add injection usage details"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('injectionUseDetails')}
                          </div>

                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="injectionUseAssistanceDetails" className={classNames({ 'p-error': errors.injectionUseAssistanceDetails })}>
                                {ASSISTANCE_REQUIRED_MESSAGE}
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="injectionUseAssistanceDetails" control={control}

                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Describe any assistance required for injection use"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('injectionUseAssistanceDetails')}
                          </div>
                        </>
                      )}
                  </div>) : (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any injection use: </strong>{sectionData?.anyInjectionUse ? YES : NO}</div>
                    {
                      sectionData?.anyInjectionUse && (
                        <>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Injection use details: </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.injectionUseDetails || NA)}</span>
                          </div>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>{ASSISTANCE_REQUIRED_MESSAGE} </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.injectionUseAssistanceDetails || NA)}</span>
                          </div>
                        </>
                      )
                    }
                  </div>
                )
              }
            </div>

            <div className="flex-column flex-wrap ">
              <Divider><Tag severity="warning">CPAP/BIPAP</Tag></Divider>
              {
                enableEditing ? (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <label className={classNames({ 'p-error': errors.anyCPAP_BiPAP })}>Any CPAP/BIPAP usage:</label>
                      <div className="inline-flex m-2 span-color-white">
                        <Controller
                          name="anyCPAP_BiPAP"
                          control={control}
                          render={({ field }) => (
                            <ToggleButton onLabel="Yes" offLabel="No"
                              {...field}
                              checked={field.value}
                              onChange={(e) => field.onChange(e.value)} />
                          )}
                        />
                      </div>
                      {getFormErrorMessage('anyCPAP_BiPAP')}
                    </div>

                    {
                      watchAnyCPAP_BiPAP && (
                        <>
                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="CPAP_BiPAPDetails" className={classNames({ 'p-error': errors.CPAP_BiPAPDetails })}>
                                Add CPAP/BiPAP usage details:
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="CPAP_BiPAPDetails" control={control}
                                rules={{ required: 'CPAP/BiPAP usage details is required.' }}
                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Add CPAP/BiPAP usage details"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('CPAP_BiPAPDetails')}
                          </div>

                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="CPAP_BiPAPAssistanceDetails" className={classNames({ 'p-error': errors.CPAP_BiPAPAssistanceDetails })}>
                                {ASSISTANCE_REQUIRED_MESSAGE}
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="CPAP_BiPAPAssistanceDetails" control={control}

                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Describe any assistance required for CPAP/BIPAP use"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('CPAP_BiPAPAssistanceDetails')}
                          </div>
                        </>
                      )}
                  </div>) : (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any CPAP/BiPAP use: </strong>{sectionData?.anyCPAP_BiPAP ? YES : NO}</div>
                    {
                      sectionData?.anyCPAP_BiPAP && (
                        <>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>CPAP/BiPAP use details: </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.CPAP_BiPAPDetails || NA)}</span>
                          </div>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>{ASSISTANCE_REQUIRED_MESSAGE} </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.CPAP_BiPAPAssistanceDetails || NA)}</span>
                          </div>
                        </>
                      )
                    }
                  </div>
                )
              }
            </div>

            <div className="flex-column flex-wrap ">
              <Divider><Tag severity="warning">Nebulizer</Tag></Divider>
              {
                enableEditing ? (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <label className={classNames({ 'p-error': errors.anyNebulizerUse })}>Any Nebulizer usage:</label>
                      <div className="inline-flex m-2 span-color-white">
                        <Controller
                          name="anyNebulizerUse"
                          control={control}
                          render={({ field }) => (
                            <ToggleButton onLabel="Yes" offLabel="No"
                              {...field}
                              checked={field.value}
                              onChange={(e) => field.onChange(e.value)} />
                          )}
                        />
                      </div>
                      {getFormErrorMessage('anyNebulizerUse')}
                    </div>

                    {
                      watchAnyNebulizerUse && (
                        <>
                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="nebulizerUseDetails" className={classNames({ 'p-error': errors.nebulizerUseDetails })}>
                                Add Nebulizer usage details:
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="nebulizerUseDetails" control={control}
                                rules={{ required: 'Nebulizer usage details is required.' }}
                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Add Nebulizer usage details"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('nebulizerUseDetails')}
                          </div>

                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="nebulizerUseAssistanceDetails" className={classNames({ 'p-error': errors.nebulizerUseAssistanceDetails })}>
                                {ASSISTANCE_REQUIRED_MESSAGE}
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="nebulizerUseAssistanceDetails" control={control}

                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Describe any assistance required for nebulizer use"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('nebulizerUseAssistanceDetails')}
                          </div>
                        </>
                      )}
                  </div>) : (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any Nebulizer use: </strong>{sectionData?.anyNebulizerUse ? YES : NO}</div>
                    {
                      sectionData?.anyNebulizerUse && (
                        <>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Nebulizer use details: </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.nebulizerUseDetails || NA)}</span>
                          </div>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>{ASSISTANCE_REQUIRED_MESSAGE} </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.nebulizerUseAssistanceDetails || NA)}</span>
                          </div>
                        </>
                      )
                    }
                  </div>
                )
              }
            </div>

            <div className="flex-column flex-wrap ">
              <Divider><Tag severity="warning">Range of motion</Tag></Divider>
              {
                enableEditing ? (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <span className="flex align-items-center gap-2 mb-2">
                        <label className={classNames({ 'p-error': errors.anyRangeOfMotionExercises })}>Any Range of motion exercises</label>
                        <span className="p-float-label" style={{ cursor: 'pointer' }}
                          onClick={() => { showHelpMessage && showHelpMessage(RESIDENT_CARE_PLAN_TYPE_MESSAGES.TREATMENT_RANGE_OF_MOTION) }}>
                          <i className="pi pi-info-circle"></i>
                        </span>
                      </span>
                      <div className="inline-flex m-2 span-color-white">
                        <Controller
                          name="anyRangeOfMotionExercises"
                          control={control}
                          render={({ field }) => (
                            <ToggleButton onLabel="Yes" offLabel="No"
                              {...field}
                              checked={field.value}
                              onChange={(e) => field.onChange(e.value)} />
                          )}
                        />
                      </div>
                      {getFormErrorMessage('anyRangeOfMotionExercises')}
                    </div>

                    {
                      watchAnyRangeOfMotionExercises && (
                        <>
                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="rangeOfMotionExercisesDetails" className={classNames({ 'p-error': errors.rangeOfMotionExercisesDetails })}>
                                Add Range of motion exercises details:
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="rangeOfMotionExercisesDetails" control={control}
                                rules={{ required: 'Range of motion exercises details is required.' }}
                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Add Range of motion exercises details"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('rangeOfMotionExercisesDetails')}
                          </div>

                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="rangeOfMotionExercisesAssistanceDetails" className={classNames({ 'p-error': errors.rangeOfMotionExercisesAssistanceDetails })}>
                                {ASSISTANCE_REQUIRED_MESSAGE}
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="rangeOfMotionExercisesAssistanceDetails" control={control}

                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Describe any assistance required for range of motion exercises"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('rangeOfMotionExercisesAssistanceDetails')}
                          </div>
                        </>
                      )}
                  </div>) : (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any Range of motion Exercises: </strong>{sectionData?.anyRangeOfMotionExercises ? YES : NO}</div>
                    {
                      sectionData?.anyRangeOfMotionExercises && (
                        <>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Range of motion Exercises Details: </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.rangeOfMotionExercisesDetails || NA)}</span>
                          </div>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>{ASSISTANCE_REQUIRED_MESSAGE} </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.rangeOfMotionExercisesAssistanceDetails || NA)}</span>
                          </div>
                        </>
                      )
                    }
                  </div>
                )
              }
            </div>

            <div className="flex-column flex-wrap ">
              <Divider><Tag severity="warning">PT/OT/ST</Tag></Divider>
              {
                enableEditing ? (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <label className={classNames({ 'p-error': errors.anyPT_OT_ST_Therapies })}>Any PT/OT/ST therapies:</label>
                      <div className="inline-flex m-2 span-color-white">
                        <Controller
                          name="anyPT_OT_ST_Therapies"
                          control={control}
                          render={({ field }) => (
                            <ToggleButton onLabel="Yes" offLabel="No"
                              {...field}
                              checked={field.value}
                              onChange={(e) => field.onChange(e.value)} />
                          )}
                        />
                      </div>
                      {getFormErrorMessage('anyPT_OT_ST_Therapies')}
                    </div>

                    {
                      watchAnyPT_OT_ST_Therapies && (
                        <>
                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="PT_OT_ST_TherapiesDetails" className={classNames({ 'p-error': errors.PT_OT_ST_TherapiesDetails })}>
                                Add PT/OT/ST therapies details:
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="PT_OT_ST_TherapiesDetails" control={control}
                                rules={{ required: 'PT/OT/ST therapies details is required.' }}
                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Add PT/OT/ST therapies details"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('PT_OT_ST_TherapiesDetails')}
                          </div>

                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="PT_OT_ST_TherapiesAssistanceDetails" className={classNames({ 'p-error': errors.PT_OT_ST_TherapiesAssistanceDetails })}>
                                {ASSISTANCE_REQUIRED_MESSAGE}
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="PT_OT_ST_TherapiesAssistanceDetails" control={control}

                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Describe any assistance required for PT/OT/ST Therapies"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('PT_OT_ST_TherapiesAssistanceDetails')}
                          </div>
                        </>
                      )}
                  </div>) : (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any PT/OT/ST therapies: </strong>{sectionData?.anyPT_OT_ST_Therapies ? YES : NO}</div>
                    {
                      sectionData?.anyPT_OT_ST_Therapies && (
                        <>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>PT/OT/ST therapies Details: </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.PT_OT_ST_TherapiesDetails || NA)}</span>
                          </div>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>{ASSISTANCE_REQUIRED_MESSAGE} </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.PT_OT_ST_TherapiesAssistanceDetails || NA)}</span>
                          </div>
                        </>
                      )
                    }
                  </div>
                )
              }
            </div>

            <div className="flex-column flex-wrap ">
              <Divider><Tag severity="warning">Nurse Delegation</Tag></Divider>
              {
                enableEditing ? (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <label className={classNames({ 'p-error': errors.anyNurseDelegatedProcedures })}>Any Nurse Delegation for Treatments/Therapies:</label>
                      <div className="inline-flex m-2 span-color-white">
                        <Controller
                          name="anyNurseDelegatedProcedures"
                          control={control}
                          render={({ field }) => (
                            <ToggleButton onLabel="Yes" offLabel="No"
                              {...field}
                              checked={field.value}
                              onChange={(e) => field.onChange(e.value)} />
                          )}
                        />
                      </div>
                      {getFormErrorMessage('anyNurseDelegatedProcedures')}
                    </div>

                    {
                      watchAnyNurseDelegatedProcedures && (
                        <>
                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="nurseDelegatedProceduresDetails" className={classNames({ 'p-error': errors.nurseDelegatedProceduresDetails })}>
                                Add Nurse delegation details:
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="nurseDelegatedProceduresDetails" control={control}
                                rules={{ required: 'Nurse delegation details is required.' }}
                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Add Nurse delegation details"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('nurseDelegatedProceduresDetails')}
                          </div>

                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="nurseDelegatedProceduresAssistanceDetails" className={classNames({ 'p-error': errors.nurseDelegatedProceduresAssistanceDetails })}>
                                {ASSISTANCE_REQUIRED_MESSAGE}
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="nurseDelegatedProceduresAssistanceDetails" control={control}

                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Describe any assistance required for nurse delegation"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('nurseDelegatedProceduresAssistanceDetails')}
                          </div>
                        </>
                      )}
                  </div>) : (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any Nurse delegation use: </strong>{sectionData?.anyNurseDelegatedProcedures ? YES : NO}</div>
                    {
                      sectionData?.anyNurseDelegatedProcedures && (
                        <>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Nurse delegation use details: </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.nurseDelegatedProceduresDetails || NA)}</span>
                          </div>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>{ASSISTANCE_REQUIRED_MESSAGE} </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.nurseDelegatedProceduresAssistanceDetails || NA)}</span>
                          </div>
                        </>
                      )
                    }
                  </div>
                )
              }
            </div>

            <div className="flex-column flex-wrap ">
              <Divider><Tag severity="warning">Other health issues</Tag></Divider>
              {
                enableEditing ? (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <label className={classNames({ 'p-error': errors.anyOtherTreatmentPrograms })}>Any other treatment programs:</label>
                      <div className="inline-flex m-2 span-color-white">
                        <Controller
                          name="anyOtherTreatmentPrograms"
                          control={control}
                          render={({ field }) => (
                            <ToggleButton onLabel="Yes" offLabel="No"
                              {...field}
                              checked={field.value}
                              onChange={(e) => field.onChange(e.value)} />
                          )}
                        />
                      </div>
                      {getFormErrorMessage('anyOtherTreatmentPrograms')}
                    </div>

                    {
                      watchAnyOtherTreatmentPrograms && (
                        <>
                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="otherTreatmentProgramsDetails" className={classNames({ 'p-error': errors.otherTreatmentProgramsDetails })}>
                                Add other treatment programs details:
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="otherTreatmentProgramsDetails" control={control}
                                rules={{ required: 'Other treatment programs details is required.' }}
                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Add other treatment programs details"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('otherTreatmentProgramsDetails')}
                          </div>

                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="otherTreatmentProgramsAssistanceDetails" className={classNames({ 'p-error': errors.otherTreatmentProgramsAssistanceDetails })}>
                                {ASSISTANCE_REQUIRED_MESSAGE}
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="otherTreatmentProgramsAssistanceDetails" control={control}

                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Describe any assistance required for other treatment programs"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('otherTreatmentProgramsAssistanceDetails')}
                          </div>
                        </>
                      )}
                  </div>) : (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any other treatment programs: </strong>{sectionData?.anyOtherTreatmentPrograms ? YES : NO}</div>
                    {
                      sectionData?.anyOtherTreatmentPrograms && (
                        <>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Other treatment programs details: </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.otherTreatmentProgramsDetails || NA)}</span>
                          </div>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>{ASSISTANCE_REQUIRED_MESSAGE} </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.otherTreatmentProgramsAssistanceDetails || NA)}</span>
                          </div>
                        </>
                      )
                    }
                  </div>
                )
              }
            </div>

            <div className="flex-column flex-wrap ">
              <Divider><Tag severity="warning">Home Health Therapy</Tag></Divider>
              {
                enableEditing ? (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <label className={classNames({ 'p-error': errors.anyHomeHealthServices })}>Any home health services:</label>
                      <div className="inline-flex m-2 span-color-white">
                        <Controller
                          name="anyHomeHealthServices"
                          control={control}
                          render={({ field }) => (
                            <ToggleButton onLabel="Yes" offLabel="No"
                              {...field}
                              checked={field.value}
                              onChange={(e) => field.onChange(e.value)} />
                          )}
                        />
                      </div>
                      {getFormErrorMessage('anyHomeHealthServices')}
                    </div>

                    {
                      watchAnyHomeHealthServices && (
                        <>
                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="homeHealthServicesDetails" className={classNames({ 'p-error': errors.homeHealthServicesDetails })}>
                                Add home health services details:
                              </label>
                              <span className="p-float-label" style={{ cursor: 'pointer' }}
                                onClick={() => { showHelpMessage && showHelpMessage(RESIDENT_CARE_PLAN_TYPE_MESSAGES.TREATMENT_HOME_HEALTH) }}>
                                <i className="pi pi-info-circle"></i>
                              </span>
                            </span>
                            <span className="p-float-label">
                              <Controller name="homeHealthServicesDetails" control={control}
                                rules={{ required: 'Home health services details is required.' }}
                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Add home health services details"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('homeHealthServicesDetails')}
                          </div>

                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="homeHealthServicesAssistanceDetails" className={classNames({ 'p-error': errors.homeHealthServicesAssistanceDetails })}>
                                {ASSISTANCE_REQUIRED_MESSAGE}
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="homeHealthServicesAssistanceDetails" control={control}

                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Describe any assistance required for home health services"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('homeHealthServicesAssistanceDetails')}
                          </div>
                        </>
                      )}
                  </div>) : (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any home health services use: </strong>{sectionData?.anyHomeHealthServices ? YES : NO}</div>
                    {
                      sectionData?.anyHomeHealthServices && (
                        <>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Home health services use details: </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.homeHealthServicesDetails || NA)}</span>
                          </div>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>{ASSISTANCE_REQUIRED_MESSAGE} </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.homeHealthServicesAssistanceDetails || NA)}</span>
                          </div>
                        </>
                      )
                    }
                  </div>
                )
              }
            </div>

            <div className="flex-column flex-wrap ">
              <Divider><Tag severity="warning">Adult Day health program</Tag></Divider>
              {
                enableEditing ? (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <label className={classNames({ 'p-error': errors.anyAdultDayHealthPrograms })}>Any adult day health programs:</label>
                      <div className="inline-flex m-2 span-color-white">
                        <Controller
                          name="anyAdultDayHealthPrograms"
                          control={control}
                          render={({ field }) => (
                            <ToggleButton onLabel="Yes" offLabel="No"
                              {...field}
                              checked={field.value}
                              onChange={(e) => field.onChange(e.value)} />
                          )}
                        />
                      </div>
                      {getFormErrorMessage('anyAdultDayHealthPrograms')}
                    </div>

                    {
                      watchAnyAdultDayHealthPrograms && (
                        <>
                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="adultDayHealthProgramsDetails" className={classNames({ 'p-error': errors.adultDayHealthProgramsDetails })}>
                                Add adult day health programs details:
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="adultDayHealthProgramsDetails" control={control}
                                rules={{ required: 'Adult day health programs details are required.' }}
                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Add adult day health programs details"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('adultDayHealthProgramsDetails')}
                          </div>

                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="adultDayHealthProgramsAssistanceDetails" className={classNames({ 'p-error': errors.adultDayHealthProgramsAssistanceDetails })}>
                                {ASSISTANCE_REQUIRED_MESSAGE}
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="adultDayHealthProgramsAssistanceDetails" control={control}

                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Describe any assistance required for adult day health programs"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('adultDayHealthProgramsAssistanceDetails')}
                          </div>
                        </>
                      )}
                  </div>) : (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any adult day health programs use: </strong>{sectionData?.anyAdultDayHealthPrograms ? YES : NO}</div>
                    {
                      sectionData?.anyAdultDayHealthPrograms && (
                        <>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Adult day health programs use details: </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.adultDayHealthProgramsDetails || NA)}</span>
                          </div>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>{ASSISTANCE_REQUIRED_MESSAGE} </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.adultDayHealthProgramsAssistanceDetails || NA)}</span>
                          </div>
                        </>
                      )
                    }
                  </div>
                )
              }
            </div>

            <div className="flex-column flex-wrap ">
              <Divider><Tag severity="warning">Hospice details</Tag></Divider>
              {
                enableEditing ? (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <label className={classNames({ 'p-error': errors.anyHospiceAgencyServices })}>Any hospice agency services:</label>
                      <div className="inline-flex m-2 span-color-white">
                        <Controller
                          name="anyHospiceAgencyServices"
                          control={control}
                          render={({ field }) => (
                            <ToggleButton onLabel="Yes" offLabel="No"
                              {...field}
                              checked={field.value}
                              onChange={(e) => field.onChange(e.value)} />
                          )}
                        />
                      </div>
                      {getFormErrorMessage('anyHospiceAgencyServices')}
                    </div>

                    {
                      watchAnyHospiceAgencyServices && (
                        <>
                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="hospiceAgencyServicesDetails" className={classNames({ 'p-error': errors.hospiceAgencyServicesDetails })}>
                                Add hospice agency services details:
                              </label>
                              <span className="p-float-label" style={{ cursor: 'pointer' }}
                                onClick={() => { showHelpMessage && showHelpMessage(RESIDENT_CARE_PLAN_TYPE_MESSAGES.TREATMENT_HOSPICE_PLAN) }}>
                                <i className="pi pi-info-circle"></i>
                              </span>
                            </span>
                            <span className="p-float-label">
                              <Controller name="hospiceAgencyServicesDetails" control={control}
                                rules={{ required: 'Hospice agency services details is required.' }}
                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Add hospice agency services details"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('hospiceAgencyServicesDetails')}
                          </div>

                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="hospiceAgencyServicesAssistanceDetails" className={classNames({ 'p-error': errors.hospiceAgencyServicesAssistanceDetails })}>
                                {ASSISTANCE_REQUIRED_MESSAGE}
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="hospiceAgencyServicesAssistanceDetails" control={control}

                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Describe any assistance required for hospice agency services"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('hospiceAgencyServicesAssistanceDetails')}
                          </div>
                        </>
                      )}
                  </div>) : (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any hospice agency services use: </strong>{sectionData?.anyHospiceAgencyServices ? YES : NO}</div>
                    {
                      sectionData?.anyHospiceAgencyServices && (
                        <>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Hospice agency services details: </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.hospiceAgencyServicesDetails || NA)}</span>
                          </div>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>{ASSISTANCE_REQUIRED_MESSAGE} </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.hospiceAgencyServicesAssistanceDetails || NA)}</span>
                          </div>
                        </>
                      )
                    }
                  </div>
                )
              }
            </div>

            <div className="flex-column flex-wrap ">
              <Divider><Tag severity="warning">Other Programs</Tag></Divider>
              {
                enableEditing ? (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <label className={classNames({ 'p-error': errors.anyOtherProgramServices })}>Any other program services:</label>
                      <div className="inline-flex m-2 span-color-white">
                        <Controller
                          name="anyOtherProgramServices"
                          control={control}
                          render={({ field }) => (
                            <ToggleButton onLabel="Yes" offLabel="No"
                              {...field}
                              checked={field.value}
                              onChange={(e) => field.onChange(e.value)} />
                          )}
                        />
                      </div>
                      {getFormErrorMessage('anyOtherProgramServices')}
                    </div>

                    {
                      watchAnyOtherProgramServices && (
                        <>
                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="otherProgramServicesDetails" className={classNames({ 'p-error': errors.otherProgramServicesDetails })}>
                                Add other program services details:
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="otherProgramServicesDetails" control={control}
                                rules={{ required: 'Other program services details is required.' }}
                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Add other program services details"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('otherProgramServicesDetails')}
                          </div>

                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="otherProgramServicesAssistanceDetails" className={classNames({ 'p-error': errors.otherProgramServicesAssistanceDetails })}>
                                {ASSISTANCE_REQUIRED_MESSAGE}
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="otherProgramServicesAssistanceDetails" control={control}

                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Describe any assistance required for other program services"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('otherProgramServicesAssistanceDetails')}
                          </div>
                        </>
                      )}
                  </div>) : (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any other program services use: </strong>{sectionData?.anyOtherProgramServices ? YES : NO}</div>
                    {
                      sectionData?.anyOtherProgramServices && (
                        <>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Other program services use details: </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.otherProgramServicesDetails || NA)}</span>
                          </div>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>{ASSISTANCE_REQUIRED_MESSAGE} </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.otherProgramServicesAssistanceDetails || NA)}</span>
                          </div>
                        </>
                      )
                    }
                  </div>
                )
              }
            </div>

            {/* Meaningful day intervention UI hidden for both provider and nurse NCP pages per request. */}

            <div className="flex-column flex-wrap ">
              <Divider><Tag severity="warning">Physical Enablers</Tag></Divider>
              {
                enableEditing ? (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <span className="flex align-items-center gap-2 mb-2">
                        <label htmlFor="anyPhysicalEnablers" className={classNames({ 'p-error': errors.anyPhysicalEnablers })}>
                          Add physical enablers details:
                        </label>
                      </span>
                      <div className="inline-flex m-2 span-color-white">
                        <Controller
                          name="anyPhysicalEnablers"
                          control={control}
                          render={({ field }) => (
                            <ToggleButton onLabel="Yes" offLabel="No"
                              {...field}
                              checked={field.value}
                              onChange={(e) => field.onChange(e.value)} />
                          )}
                        />
                      </div>
                      {getFormErrorMessage('anyPhysicalEnablers')}
                    </div>

                    {
                      watchAnyPhysicalEnablers && (
                        <>
                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="physicalEnablersDetails" className={classNames({ 'p-error': errors.physicalEnablersDetails })}>
                                Add physical enablers details:
                              </label>
                              <span className="p-float-label" style={{ cursor: 'pointer' }}
                                onClick={() => { showHelpMessage && showHelpMessage(RESIDENT_CARE_PLAN_TYPE_MESSAGES.TREATMENT_PHYSICAL_ENABLERS) }}>
                                <i className="pi pi-info-circle"></i>
                              </span>
                            </span>
                            <span className="p-float-label">
                              <Controller name="physicalEnablersDetails" control={control}
                                rules={{ required: 'Physical enablers details are required.' }}
                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Add physical enablers details"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('physicalEnablersDetails')}
                          </div>

                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="physicalEnablersAssistanceDetails" className={classNames({ 'p-error': errors.physicalEnablersAssistanceDetails })}>
                                {ASSISTANCE_REQUIRED_MESSAGE}
                              </label>
                              <span className="p-float-label" style={{ cursor: 'pointer' }}
                                onClick={() => { showHelpMessage && showHelpMessage(RESIDENT_CARE_PLAN_TYPE_MESSAGES.TREATMENT_PHYSICAL_ENABLERS_ASSISTANCE) }}>
                                <i className="pi pi-info-circle"></i>
                              </span>
                            </span>
                            <span className="p-float-label">
                              <Controller name="physicalEnablersAssistanceDetails" control={control}

                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Describe any assistance required for physical enablers"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('physicalEnablersAssistanceDetails')}
                          </div>
                        </>
                      )}
                  </div>) : (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any physical enablers use: </strong>{sectionData?.anyPhysicalEnablers ? YES : NO}</div>
                    {
                      sectionData?.anyPhysicalEnablers && (
                        <>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Physical enablers use details: </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.physicalEnablersDetails || NA)}</span>
                          </div>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>{ASSISTANCE_REQUIRED_MESSAGE} </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.physicalEnablersAssistanceDetails || NA)}</span>
                          </div>
                        </>
                      )
                    }
                  </div>
                )
              }
            </div>

            <div className="flex-column flex-wrap ">
              <Divider><Tag severity="warning">Refusal Plan</Tag></Divider>
              {
                enableEditing ? (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <label className={classNames({ 'p-error': errors.anyTreatmentRefusalPlan })}>Any treatment refusal plan:</label>
                      <div className="inline-flex m-2 span-color-white">
                        <Controller
                          name="anyTreatmentRefusalPlan"
                          control={control}
                          render={({ field }) => (
                            <ToggleButton onLabel="Yes" offLabel="No"
                              {...field}
                              checked={field.value}
                              onChange={(e) => field.onChange(e.value)} />
                          )}
                        />
                      </div>
                      {getFormErrorMessage('anyTreatmentRefusalPlan')}
                    </div>

                    {
                      watchAnyTreatmentRefusalPlan && (
                        <>
                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="treatmentRefusalPlanDetails" className={classNames({ 'p-error': errors.treatmentRefusalPlanDetails })}>
                                Add treatment refusal plan details:
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="treatmentRefusalPlanDetails" control={control}
                                rules={{ required: 'Treatment refusal plan details are required.' }}
                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Add treatment refusal plan details"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('treatmentRefusalPlanDetails')}
                          </div>

                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="treatmentRefusalPlanAssistanceDetails" className={classNames({ 'p-error': errors.treatmentRefusalPlanAssistanceDetails })}>
                                {ASSISTANCE_REQUIRED_MESSAGE}
                              </label>
                              <span className="p-float-label" style={{ cursor: 'pointer' }}
                                onClick={() => { showHelpMessage && showHelpMessage(RESIDENT_CARE_PLAN_TYPE_MESSAGES.TREATMENT_REFUSAL_ASSISTANCE) }}>
                                <i className="pi pi-info-circle"></i>
                              </span>
                            </span>
                            <span className="p-float-label">
                              <Controller name="treatmentRefusalPlanAssistanceDetails" control={control}

                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Describe any assistance required for treatment refusal plan"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('treatmentRefusalPlanAssistanceDetails')}
                          </div>
                        </>
                      )}
                  </div>) : (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any treatment refusal plan: </strong>{sectionData?.anyTreatmentRefusalPlan ? YES : NO}</div>
                    {
                      sectionData?.anyTreatmentRefusalPlan && (
                        <>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Treatment refusal plan details: </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.treatmentRefusalPlanDetails || NA)}</span>
                          </div>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>{ASSISTANCE_REQUIRED_MESSAGE} </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.treatmentRefusalPlanAssistanceDetails || NA)}</span>
                          </div>
                        </>
                      )
                    }
                  </div>
                )
              }
            </div>
          </form>
        </div>
      </Card >
    </div >
  );
};

export default TreatmentProgramSection;


